// i18n/index.ts — ponytail: minimal useTranslation shim over the widget's own
// JSON catalogs; studio has i18next v10 and no react-i18next, and merging ~200
// keys into locales/{en,es,tr}.js buys nothing. Upgrade path: merge into studio
// locales if these strings ever need the es locale for real.
//
// Extends the plan's shim with `i18n.exists()` (job-name lookups in JobImageCard/
// JobModal/*Modal call it unconditionally — the plan's snippet omitted it, which
// would throw at render) and `_one`/`_other` plural-suffix resolution for the 3
// keys that pass `{count}` (widget.jobsTotal, jobCard.narrationDone,
// questionBatch.generatedCount) — the plan flagged this as a "check and add if
// any exist" step; they do exist in i18n/locales/{en,tr}.json.
import { useSyncExternalStore } from "react";
import studioI18n from "i18next";
import en from "./locales/en.json";
import tr from "./locales/tr.json";
import es from "./locales/es.json";

const dicts: Record<string, any> = { en, tr, es };
const listeners = new Set<() => void>();
const short = (l: any): string => String(l || "").slice(0, 2);
const readStored = (): string | null => {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem("widget_lang") : null;
  } catch {
    return null;
  }
};
// Precedence: the user's explicit pick in the widget menu → ?lng= → the
// editor's own UI language → tr.
let lang: string =
  readStored() ||
  new URLSearchParams(window.location.search).get("lng") ||
  short(studioI18n.language) ||
  "tr";
if (!dicts[lang]) lang = "en"; // anything without a catalog falls back to en

// Follow the editor's language switches unless the user pinned one here.
if (studioI18n && typeof studioI18n.on === "function") {
  studioI18n.on("languageChanged", (l: string) => {
    if (readStored()) return;
    const next = dicts[short(l)] ? short(l) : "en";
    if (next === lang) return;
    lang = next;
    listeners.forEach((f) => f());
  });
}
const subscribe = (cb: () => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

const rawLookup = (key: string, dict: any): any =>
  key.split(".").reduce((o: any, k) => (o == null ? o : o[k]), dict);

const lookup = (key: string): any => rawLookup(key, dicts[lang]) ?? rawLookup(key, dicts.en);

const interpolate = (s: string, opts?: Record<string, any>) =>
  opts ? s.replace(/\{\{(\w+)\}\}/g, (_, k) => (opts[k] != null ? String(opts[k]) : "")) : s;

export function useTranslation() {
  useSyncExternalStore(subscribe, () => lang);
  return {
    t: (key: string, opts?: Record<string, any>): string => {
      let resolvedKey = key;
      if (opts && typeof opts.count === "number") {
        const suffixed = `${key}_${opts.count === 1 ? "one" : "other"}`;
        if (lookup(suffixed) !== undefined) resolvedKey = suffixed;
      }
      const v = lookup(resolvedKey);
      if (typeof v !== "string") return (opts && (opts.defaultValue as string)) || key;
      return interpolate(v, opts);
    },
    i18n: {
      language: lang,
      exists: (key: string): boolean => typeof lookup(key) === "string",
      changeLanguage: (l: string) => {
        lang = dicts[l] ? l : "en";
        try {
          localStorage.setItem("widget_lang", lang);
        } catch {}
        listeners.forEach((f) => f());
      },
    },
  };
}
